/**
 * File d'actions hors ligne pour le mode foire (S1-c, ARTO-4, § 7.2 « le
 * salon tolère un réseau capricieux »). Trois gestes tactiles (Réserver,
 * Noter une demande, Vendre) écrivent d'abord dans cette file persistée en
 * `localStorage` ; `rejouerFileHorsLigne` la vide dès que le réseau revient,
 * sans jamais perdre une action silencieusement (chaque échec reste en
 * file et remonte une erreur lisible plutôt que de disparaître).
 *
 * Cloisonnement (ARTO-52, audit MVP du 23/09 § 2) : une file par compte ET
 * par galerie. Chaque action porte aussi son identité ; le rejeu n'exécute
 * que celles de l'identité et de la galerie courantes. À la déconnexion la
 * file du compte reste en place — elle attend son propriétaire, jamais un
 * autre compte.
 *
 * Stockage (constat N2) : UNE CLÉ PAR ACTION,
 * `artosera-foire-file:<compteId>:<galerieSlug>:<actionId>` ; la file est
 * dérivée en parcourant les clés (`stockageParCle.ts`). Deux onglets qui
 * ajoutent en même temps ne s'écrasent plus.
 */

import {
  capturerPortee,
  estPorteeObsolete,
  exigerActuelle,
  identiteCourante,
} from '../../auth/identite.js';
import {
  clesDuStockage,
  ecrireEnregistrement,
  lireCleBrute,
  lireEnregistrements,
  mettreAJourEnregistrement,
  nonDurable,
  oublierMemoireDeRepli,
  retirerCleBrute,
  retirerEnregistrement,
  retraitEnAttente,
} from './stockageParCle.js';

/**
 * `vente` prépare une facture (module `facturation`) ; `vente-simple` marque
 * l'œuvre vendue sans facture (cœur, D21) — le geste d'une galerie sans
 * facturation.
 */
// Les intérêts partagent la portée et la clé durable des gestes du stand.
export type TypeActionFoire =
  'reservation' | 'demande' | 'vente' | 'vente-simple' | 'offre' | 'interet-presentation';

/** Le compte et la galerie auxquels une file (et chacune de ses actions) appartient. */
export interface PorteeFile {
  readonly compteId: string;
  readonly galerieSlug: string;
}

export interface ActionFoireEnAttente<TCorps = unknown> extends PorteeFile {
  readonly id: string;
  /**
   * Identifiant d'OPÉRATION durable (ARTO-52) : chaque étape réseau de
   * l'action l'envoie, suffixé, en `Idempotency-Key`. Un rejeu — après une
   * réponse perdue, ou en double depuis un autre écran — reçoit le résultat de
   * SA propre opération au lieu d'en produire une seconde. Absent des actions
   * d'avant ARTO-52 : `operationDe` retombe alors sur `id`, lui aussi durable.
   */
  readonly operationId?: string;
  readonly type: TypeActionFoire;
  readonly corps: TCorps;
  readonly creeeLe: string;
  /**
   * Rang d'ajout, strictement croissant dans un onglet (horodatage en
   * microsecondes, départagé par un compteur) : l'ordre de la file ne
   * dépend plus de l'ordre des clés du stockage.
   */
  readonly rang?: number;
  readonly tentatives: number;
  readonly derniereErreur: string | null;
  /**
   * L'action ne peut plus être rejouée automatiquement : le serveur a changé
   * sous elle (brouillon modifié, facture déjà émise par ailleurs). Elle reste
   * en file, n'est plus tentée, et le stand demande de la vérifier en régie.
   */
  readonly aVerifier?: string | null;
}

/** La clé GLOBALE d'avant ARTO-52 : sans compte ni galerie. Migrée puis retirée. */
export const ANCIENNE_CLE_FILE = 'artosera-foire-file-attente';

const PREFIXE_CLE = 'artosera-foire-file';

/** L'identifiant d'opération d'une action (voir `operationId`). */
export function operationDe(action: ActionFoireEnAttente): string {
  return action.operationId ?? action.id;
}

function nouvelIdentifiant(): string {
  const crypto = globalThis.crypto as Crypto | undefined;
  if (crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Préfixe des clés de la file d'une portée : `…:<compteId>:<galerieSlug>:`. */
export function prefixeFile(portee: PorteeFile): string {
  return `${PREFIXE_CLE}:${portee.compteId}:${portee.galerieSlug}:`;
}

/** La clé d'UNE action de la file. */
export function cleAction(portee: PorteeFile, id: string): string {
  return `${prefixeFile(portee)}${id}`;
}

/** Identifie la file d'une portée (verrous, tests). */
export function cleFile(portee: PorteeFile): string {
  return `${PREFIXE_CLE}:${portee.compteId}:${portee.galerieSlug}`;
}

/** La portée de l'identité courante (compte + galerie ouverte), s'il y en a une. */
export function porteeCourante(): PorteeFile | null {
  const identite = identiteCourante();
  if (!identite?.galerieSlug) return null;
  return { compteId: identite.compteId, galerieSlug: identite.galerieSlug };
}

function memePortee(a: PorteeFile, b: PorteeFile): boolean {
  return a.compteId === b.compteId && a.galerieSlug === b.galerieSlug;
}

/** Ordre de la file : rang d'ajout, puis date, puis identifiant. */
function ordonner(actions: ActionFoireEnAttente[]): ActionFoireEnAttente[] {
  return actions.sort(
    (a, b) =>
      (a.rang ?? 0) - (b.rang ?? 0) ||
      a.creeeLe.localeCompare(b.creeeLe) ||
      a.id.localeCompare(b.id),
  );
}

let dernierRang = 0;

function prochainRang(): number {
  dernierRang = Math.max(Date.now() * 1000, dernierRang + 1);
  return dernierRang;
}

/**
 * La file de `portee` n'est-elle PAS entièrement durable — action gardée en
 * mémoire, retrait en attente, ou stockage illisible — et donc en partie
 * perdue si l'onglet se ferme ?
 */
export function fileEnMemoire(portee: PorteeFile | null = porteeCourante()): boolean {
  return portee !== null && nonDurable(prefixeFile(portee));
}

function exigerPortee(portee: PorteeFile | null): PorteeFile {
  if (!portee) {
    throw new Error('Aucun compte ni galerie ouverts : action hors ligne refusée.');
  }
  return portee;
}

/**
 * Lit la file d'une portée (par défaut l'identité courante ; vide sans
 * identité), dans l'ordre d'ajout, la plus ancienne en premier.
 */
export function lireFileHorsLigne(
  portee: PorteeFile | null = porteeCourante(),
): ActionFoireEnAttente[] {
  if (!portee) return [];
  const { enregistrements } = lireEnregistrements<ActionFoireEnAttente>(prefixeFile(portee));
  return ordonner(
    [...enregistrements.values()].filter(
      (action): action is ActionFoireEnAttente =>
        typeof action === 'object' && action !== null && typeof action.id === 'string',
    ),
  );
}

/** Met à jour UNE action de la file — elle seule, et seulement si elle y est encore. */
function mettreAJourAction(
  portee: PorteeFile,
  id: string,
  maj: (action: ActionFoireEnAttente) => ActionFoireEnAttente,
): void {
  mettreAJourEnregistrement<ActionFoireEnAttente>(prefixeFile(portee), id, maj);
}

/**
 * Ajoute une action à la file de l'identité courante, jamais perdue tant
 * qu'elle n'a pas réussi. Sans identité, refuse plutôt que de ranger
 * l'action sous une clé que personne ne relira.
 */
export function empilerActionFoire<TCorps>(
  type: TypeActionFoire,
  corps: TCorps,
  portee: PorteeFile | null = porteeCourante(),
): ActionFoireEnAttente<TCorps> {
  const cible = exigerPortee(portee);
  const action: ActionFoireEnAttente<TCorps> = {
    id: `${type}-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    operationId: nouvelIdentifiant(),
    type,
    corps,
    creeeLe: new Date().toISOString(),
    rang: prochainRang(),
    tentatives: 0,
    derniereErreur: null,
    compteId: cible.compteId,
    galerieSlug: cible.galerieSlug,
  };
  // Un seul `setItem`, sur la clé de CETTE action : rien d'autre n'est relu
  // ni réécrit (constat N2).
  ecrireEnregistrement(prefixeFile(cible), action.id, action);
  // Jamais un identifiant qui ne serait rangé nulle part : relu ou refusé.
  if (!lireFileHorsLigne(cible).some((enFile) => enFile.id === action.id)) {
    throw new Error('Action hors ligne non enregistrée : ni stockage ni mémoire disponibles.');
  }
  return action;
}

/** Retire une action réussie de la file : un `removeItem` sur sa seule clé. */
export function retirerActionFoire(id: string, portee: PorteeFile | null = porteeCourante()): void {
  if (!portee) return;
  retirerEnregistrement(prefixeFile(portee), id);
}

/** Marque une tentative échouée (l'action reste en file, visible et rejouable). */
export function marquerEchecActionFoire(
  id: string,
  erreur: string,
  portee: PorteeFile | null = porteeCourante(),
): void {
  if (!portee) return;
  mettreAJourAction(portee, id, (action) => ({
    ...action,
    tentatives: action.tentatives + 1,
    derniereErreur: erreur,
  }));
}

/** Retire l'action du rejeu automatique : une personne doit la vérifier en régie. */
export function marquerAVerifier(
  id: string,
  motif: string,
  portee: PorteeFile | null = porteeCourante(),
): void {
  if (!portee) return;
  mettreAJourAction(portee, id, (action) => ({
    ...action,
    tentatives: action.tentatives + 1,
    derniereErreur: motif,
    aVerifier: motif,
  }));
}

/**
 * Levée par l'exécutant quand le serveur a changé sous l'action (constat C) :
 * elle n'est ni retirée ni retentée, mais marquée « à vérifier en régie ».
 */
export class ActionAVerifier extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ActionAVerifier';
  }
}

/**
 * Fusionne `fusion` dans le corps d'une action en attente (ex. graver le
 * `contactId` obtenu par une création de contact réussie, avant que la
 * réservation ou la facture qui en dépend échoue). Un rejeu ultérieur relit
 * cette valeur au lieu de recréer le contact : l'étape déjà réussie de la
 * chaîne n'est jamais rejouée deux fois.
 */
export function fusionnerCorpsActionFoire<TCorps>(
  id: string,
  fusion: (corps: TCorps) => TCorps,
  portee: PorteeFile | null = porteeCourante(),
): void {
  if (!portee) return;
  mettreAJourAction(portee, id, (action) => ({
    ...action,
    corps: fusion(action.corps as TCorps),
  }));
}

/**
 * Levée par l'exécutant pour ARRÊTER le rejeu sans imputer d'échec à
 * l'action : session expirée, action destinée à un autre compte, réseau
 * coupé. L'action reste en file, intacte, et les suivantes ne sont pas
 * tentées.
 */
export class ArretRejeu extends Error {
  constructor(
    readonly raison: RaisonArretRejeu,
    message: string,
  ) {
    super(message);
    this.name = 'ArretRejeu';
  }
}

export type RaisonArretRejeu =
  | 'identite_changee'
  | 'session_expiree'
  | 'compte_inattendu'
  | 'compte_different'
  | 'acces_retire'
  /** Les CGU sont à accepter (ARTO-59) : rien ne part avant. */
  | 'cgu_a_accepter'
  /** La double authentification est à activer : rien ne part avant. */
  | 'double_authentification_a_activer'
  | 'reseau';

export interface BilanRejeu {
  readonly reussies: number;
  readonly echouees: number;
  /** Pourquoi le rejeu s'est arrêté avant la fin de la file, s'il l'a fait. */
  readonly arret: RaisonArretRejeu | null;
}

/**
 * Ce que l'exécutant reçoit avec l'action : `verifier()` lève si l'identité
 * a changé depuis le lancement du rejeu. Il l'appelle AVANT CHAQUE étape
 * réseau d'une action (relecture du 23/09, constat 2) — pas seulement entre
 * deux actions.
 */
export interface EtapeRejeu {
  readonly verifier: () => void;
  /** Le signal de l'époque capturée au lancement du rejeu, à passer à chaque `fetch`. */
  readonly signal: AbortSignal;
}

/** Un seul rejeu à la fois par portée, dans cet onglet (constat 3①). */
const rejeuxEnCours = new Map<string, Promise<BilanRejeu>>();

interface VerrousNavigateur {
  request<T>(nom: string, rappel: () => Promise<T>): Promise<T>;
}

/** Et entre onglets, quand le navigateur offre `navigator.locks`. */
function sousVerrouNavigateur<T>(nom: string, rappel: () => Promise<T>): Promise<T> {
  const verrous = (globalThis.navigator as { locks?: VerrousNavigateur } | undefined)?.locks;
  if (verrous && typeof verrous.request === 'function') return verrous.request(nom, rappel);
  return rappel();
}

/**
 * Rejoue la file de `portee` (l'identité courante par défaut) dans l'ordre,
 * via `executer(action, etape)`. Continue après un rejet applicatif (l'action
 * fautive reste seule en attente, marquée, les suivantes sont tentées).
 *
 * - **Sérialisé** : un seul rejeu à la fois par portée — verrou en mémoire,
 *   plus `navigator.locks` quand il existe. Un second appel attend la fin du
 *   premier puis relit la file : deux écrans ne créent jamais deux fois la
 *   même facture.
 * - **Cloisonné** : seules les actions qui portent cette identité sont
 *   exécutées, et l'ÉPOQUE de session est capturée au lancement. Si elle
 *   change — déconnexion, autre compte, autre galerie —, `etape.verifier()`
 *   lève avant l'étape réseau suivante et le rejeu s'arrête net.
 * - **Arrêts** : `ArretRejeu` (session expirée, compte inattendu, réseau) et
 *   `PorteeObsolete` stoppent le rejeu sans marquer l'action.
 */
export interface OptionsRejeu {
  /**
   * Appelée une fois, sous le verrou, AVANT la première action — et
   * seulement s'il y a quelque chose à rejouer. Le stand y revalide la
   * session (relecture du 23/09, constat 7) ; une `ArretRejeu` levée ici
   * arrête le rejeu sans rien envoyer.
   */
  readonly preparer?: (etape: EtapeRejeu) => Promise<void>;
}

export function rejouerFileHorsLigne(
  executer: (action: ActionFoireEnAttente, etape: EtapeRejeu) => Promise<void>,
  portee: PorteeFile | null = porteeCourante(),
  options: OptionsRejeu = {},
): Promise<BilanRejeu> {
  if (!portee) return Promise.resolve({ reussies: 0, echouees: 0, arret: null });
  const cle = cleFile(portee);
  const precedent = rejeuxEnCours.get(cle) ?? Promise.resolve(null);
  const courant = precedent
    .catch(() => null)
    .then(() =>
      sousVerrouNavigateur(`artosera-foire-rejeu:${cle}`, () => rejouer(executer, portee, options)),
    );
  rejeuxEnCours.set(cle, courant);
  return courant.finally(() => {
    if (rejeuxEnCours.get(cle) === courant) rejeuxEnCours.delete(cle);
  });
}

/** Ce qui ARRÊTE le rejeu (et pourquoi), ou `null` pour une erreur de l'action. */
function raisonDArret(erreur: unknown): RaisonArretRejeu | null {
  if (estPorteeObsolete(erreur)) return 'identite_changee';
  if (erreur instanceof ArretRejeu) return erreur.raison;
  return null;
}

async function rejouer(
  executer: (action: ActionFoireEnAttente, etape: EtapeRejeu) => Promise<void>,
  portee: PorteeFile,
  options: OptionsRejeu,
): Promise<BilanRejeu> {
  let reussies = 0;
  let echouees = 0;
  const capture = capturerPortee();
  if (capture.compteId !== portee.compteId || capture.galerieSlug !== portee.galerieSlug) {
    return { reussies, echouees, arret: 'identite_changee' };
  }
  let etape: EtapeRejeu = {
    verifier: () => exigerActuelle(capture),
    signal: capture.signal,
  };

  if (
    !lireFileHorsLigne(portee).some((action) => memePortee(action, portee) && !action.aVerifier)
  ) {
    return { reussies, echouees, arret: null };
  }
  if (options.preparer) {
    try {
      await options.preparer(etape);
    } catch (erreur) {
      return { reussies, echouees, arret: raisonDArret(erreur) ?? 'reseau' };
    }
    // La préparation (revalidation de la session) a pu ouvrir une nouvelle
    // époque — droits changés pour le MÊME compte et la même galerie. Les
    // actions partent sous cette époque-là, avec les droits frais ; un autre
    // compte ou une autre galerie arrête le rejeu.
    const apres = capturerPortee();
    if (apres.compteId !== portee.compteId || apres.galerieSlug !== portee.galerieSlug) {
      return { reussies, echouees, arret: 'identite_changee' };
    }
    etape = { verifier: () => exigerActuelle(apres), signal: apres.signal };
  }

  for (const action of lireFileHorsLigne(portee)) {
    if (!memePortee(action, portee) || action.aVerifier) continue;
    try {
      etape.verifier();
      await executer(action, etape);
      etape.verifier();
      retirerActionFoire(action.id, portee);
      reussies += 1;
    } catch (erreur) {
      const arret = raisonDArret(erreur);
      if (arret) return { reussies, echouees, arret };
      if (erreur instanceof ActionAVerifier) {
        marquerAVerifier(action.id, erreur.message, portee);
        echouees += 1;
        continue;
      }
      const message = erreur instanceof Error ? erreur.message : 'erreur inconnue';
      marquerEchecActionFoire(action.id, message, portee);
      echouees += 1;
    }
  }
  return { reussies, echouees, arret: null };
}

export interface BilanMigration {
  /** Actions reprises dans la file de la portée courante. */
  readonly migrees: number;
  /** Actions mises en quarantaine : conservées, signalées, jamais rejouées. */
  readonly quarantaine: number;
}

/**
 * QUARANTAINE (relecture du 23/09, constat 9) : les actions de l'ancienne
 * file globale qui ne portent ni compte ni galerie. Impossible de savoir
 * sous quelle identité elles ont été saisies : elles ne sont JAMAIS
 * rejouées, mais leur corps est conservé intact — une clé par action,
 * `artosera-foire-quarantaine:<id>` —, pour que le support puisse les
 * récupérer, et le stand l'annonce durablement, sur la grille comme sur la
 * fiche, tant qu'elles y sont.
 */
export const PREFIXE_QUARANTAINE = 'artosera-foire-quarantaine:';

export interface ActionEnQuarantaine extends Partial<ActionFoireEnAttente> {
  readonly id: string;
  readonly misEnQuarantaineLe: string;
}

export function lireQuarantaine(): ActionEnQuarantaine[] {
  return [
    ...lireEnregistrements<ActionEnQuarantaine>(PREFIXE_QUARANTAINE).enregistrements.values(),
  ];
}

export interface BilanMigration {
  /** Actions reprises dans la file de la portée courante. */
  readonly migrees: number;
  /** Actions mises en quarantaine : conservées, signalées, jamais rejouées. */
  readonly quarantaine: number;
}

/** Ancien format de CETTE branche : un tableau par portée, `…:<compte>:<galerie>`. */
const MOTIF_TABLEAU_PAR_PORTEE = /^artosera-foire-file:[^:]+:[^:]+$/;

function porteeDe(action: Partial<ActionFoireEnAttente>): PorteeFile | null {
  return typeof action.compteId === 'string' && typeof action.galerieSlug === 'string'
    ? { compteId: action.compteId, galerieSlug: action.galerieSlug }
    : null;
}

/**
 * Range UNE ancienne action sous sa propre clé. Une action déjà rejouée et
 * retirée (retrait encore en attente du stockage) n'est pas réintroduite.
 * `true` si la destination est durable.
 */
function rangerAncienne(portee: PorteeFile, action: ActionFoireEnAttente): boolean {
  const prefixe = prefixeFile(portee);
  if (retraitEnAttente(prefixe, action.id)) return false;
  if (lireEnregistrements(prefixe).enregistrements.has(action.id)) return !nonDurable(prefixe);
  return ecrireEnregistrement(prefixe, action.id, action);
}

/**
 * Migre les anciens formats en UNE CLÉ PAR ACTION (constats 9, E et N2) :
 *
 *  - l'ancienne file GLOBALE (`artosera-foire-file-attente`) : une action qui
 *    porte une identité complète rejoint la file de CETTE identité ; une
 *    action sans identité part en quarantaine, corps conservé ;
 *  - l'ancien tableau par portée (`…:<compte>:<galerie>`) : chaque action est
 *    éclatée sous sa propre clé.
 *
 * Une source n'est retirée qu'une fois CHAQUE action recopiée durablement :
 * sous quota, la source reste, et une migration suivante reprendra.
 */
export function migrerAncienneFile(portee: PorteeFile): BilanMigration {
  let migrees = 0;
  let quarantaine = 0;

  const globale = lireCleBrute(ANCIENNE_CLE_FILE);
  if (globale) {
    let toutDurable = true;
    for (const [rang, brute] of globale.entries()) {
      const ancienne = (brute ?? {}) as Partial<ActionFoireEnAttente>;
      const sienne = typeof ancienne.id === 'string' ? porteeDe(ancienne) : null;
      if (sienne) {
        toutDurable = rangerAncienne(sienne, ancienne as ActionFoireEnAttente) && toutDurable;
        if (memePortee(sienne, portee)) migrees += 1;
      } else {
        const id =
          typeof ancienne.id === 'string' ? ancienne.id : `sans-identifiant-${String(rang)}`;
        toutDurable =
          ecrireEnregistrement(PREFIXE_QUARANTAINE, id, {
            ...ancienne,
            id,
            misEnQuarantaineLe: new Date().toISOString(),
          }) && toutDurable;
        quarantaine += 1;
      }
    }
    if (toutDurable) retirerCleBrute(ANCIENNE_CLE_FILE);
  } else if (globale !== null && globale !== undefined) {
    retirerCleBrute(ANCIENNE_CLE_FILE);
  }

  for (const cle of clesDuStockage(MOTIF_TABLEAU_PAR_PORTEE)) {
    const tableau = lireCleBrute(cle);
    if (!tableau) continue;
    const [, compteId = '', galerieSlug = ''] = cle.split(':');
    const deLaCle: PorteeFile = { compteId, galerieSlug };
    let toutDurable = true;
    for (const brute of tableau) {
      const ancienne = (brute ?? {}) as Partial<ActionFoireEnAttente>;
      if (typeof ancienne.id !== 'string') continue;
      const action = { ...deLaCle, ...ancienne } as ActionFoireEnAttente;
      const sienne = porteeDe(action) ?? deLaCle;
      toutDurable = rangerAncienne(sienne, action) && toutDurable;
      if (memePortee(sienne, portee)) migrees += 1;
    }
    if (toutDurable) retirerCleBrute(cle);
  }

  return { migrees, quarantaine };
}

/**
 * Vide entièrement la file d'une portée (l'identité courante par défaut) —
 * tests, ou réinitialisation volontaire.
 */
export function viderFileHorsLigne(portee: PorteeFile | null = porteeCourante()): void {
  if (!portee) return;
  for (const action of lireFileHorsLigne(portee))
    retirerEnregistrement(prefixeFile(portee), action.id);
}

/** Oublie tout repli mémoire (tests). */
export function oublierReplisMemoire(): void {
  oublierMemoireDeRepli();
}

// Les intérêts locaux disparaissent avec leur préparation ; les gestes du stand restent conservés.
export function purgerInteretsPresentation(portee?: PorteeFile): void {
  const prefixe = portee ? prefixeFile(portee) : `${PREFIXE_CLE}:`;
  for (const action of lireEnregistrements<ActionFoireEnAttente>(
    prefixe,
  ).enregistrements.values()) {
    if (action.type === 'interet-presentation') retirerActionFoire(action.id, action);
  }
}
